import {
  ActionIcon,
  Blockquote,
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { type TransformedValues, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconChevronRight, IconFolder } from "@tabler/icons-react";
import * as df from "date-fns";
import { useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import type {
  FileListOutput,
  FileOutput,
  FolderOutput,
  SessionWithOwnerDogOutput,
} from "@/api-types";
import { FancyInlineLoader, FancyLoader } from "@/components/FancyLoader";
import { FileUploadModal } from "@/components/FileUploadModal";
import { FolderNavigator } from "@/components/FolderNavigator";
import { Header } from "@/components/Header";
import { LibraryItem } from "@/components/LibraryItem";
import { MultilineText } from "@/components/MultilineText";
import { Portrait } from "@/components/Portrait";
import { SessionTab, SessionTabs } from "@/components/SessionTabs";
import {
  isSessionCancelled,
  renderCancelStatus,
} from "@/components/SessionUtils";
import { SurveyList } from "@/components/SurveyList";
import { useHashTab } from "@/hooks/useHashTab";
import { TrainerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";
import type { SurveyStatus } from "@/utils/survey";

type SessionWithOwner = SessionWithOwnerDogOutput;

const SessionFiles = (props: {
  session: SessionWithOwner;
  refetchSession: () => Promise<unknown>;
  fileList?: FileListOutput;
  refetchFiles: () => Promise<unknown>;
  isLoadingFiles: boolean;
}) => {
  const { session, refetchSession, fileList, refetchFiles } = props;

  const fileIds = session.fileIds ?? [];
  // In some types, folderIds may not exist in sessionType, so default to empty array
  const folderIds: string[] = [];

  const addFileToSessionMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/add-file",
  );

  const addFolderToSessionMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/add-folder",
  );

  const removeFileFromSessionMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/remove-file",
  );

  const handleAddFileToSession = async (file: FileOutput) => {
    await addFileToSessionMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        fileId: file.id,
      },
    });

    await Promise.all([refetchFiles(), refetchSession()]);
  };

  const handleAddFolderToSession = async (folder: FolderOutput | null) => {
    if (!folder) return;
    await addFolderToSessionMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        fileId: folder.id,
      },
    });

    await Promise.all([refetchFiles(), refetchSession()]);
  };

  const handleRemoveFileFromSession = async (fileId: string) => {
    await removeFileFromSessionMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        fileId: fileId,
      },
    });

    await Promise.all([refetchFiles(), refetchSession()]);
  };

  if (!fileList && fileIds.length > 0) {
    return <FancyInlineLoader />;
  }

  if (fileIds.length === 0 && folderIds.length === 0) {
    return (
      <Stack>
        <Title order={5}>Files shared with the client</Title>
        <Paper p="xl" withBorder>
          <Stack gap="md">
            <Text c="dimmed">
              No files have been shared with the client yet.
            </Text>
            <Group>
              <FolderNavigator
                onFileSelect={handleAddFileToSession}
                onFolderSelect={handleAddFolderToSession}
              >
                <Button
                  variant="outline"
                  leftSection={<IconFolder size="1.2em" />}
                  style={{ flex: 1 }}
                >
                  Pick from Library
                </Button>
              </FolderNavigator>
              <FileUploadModal onSuccess={handleAddFileToSession} />
            </Group>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack>
      <Title order={5}>Files shared with the client</Title>

      <Group>
        <FolderNavigator
          onFileSelect={handleAddFileToSession}
          onFolderSelect={handleAddFolderToSession}
        >
          <Button
            variant="outline"
            leftSection={<IconFolder size="1.2em" />}
            style={{ flex: 1 }}
          >
            Pick from Library
          </Button>
        </FolderNavigator>
        <FileUploadModal onSuccess={handleAddFileToSession} />
      </Group>

      {fileList?.files?.map((file: FileOutput) => (
        <LibraryItem
          key={file.id}
          kind="file"
          item={file}
          readonly={true}
          onDelete={handleRemoveFileFromSession}
        />
      ))}
      {fileList?.folders?.map((folder: FolderOutput) => (
        <LibraryItem
          key={folder.id}
          kind="folder"
          item={folder}
          readonly={true}
          onDelete={handleRemoveFileFromSession}
          onFolderClick={() => {}}
        />
      ))}
    </Stack>
  );
};

const SessionNotes = (props: { session: SessionWithOwner }) => {
  const { session } = props;
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [preparation, setPreparation] = useState(session.preparation);

  const futureSession =
    session.status !== "COMPLETED" && !isSessionCancelled(session.status);

  const preparationForm = useForm({
    initialValues: {
      preparation: preparation ?? "",
    },
    transformValues: (values) => ({
      preparation: values.preparation,
    }),
  });

  const saveSessionPreparationMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/update",
  );

  const onSaveSessionPreparation = async (
    values: TransformedValues<typeof preparationForm>,
  ) => {
    setShowOnboardingForm(false);
    await saveSessionPreparationMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        preparation: values.preparation,
      },
    });
    preparationForm.resetDirty();
    setPreparation(values.preparation);
  };

  return (
    <Stack w="100%">
      {session.purpose && (
        <Blockquote p="xs">
          <Title order={5}>Client&apos;s session purpose</Title>
          <MultilineText text={session.purpose} />
        </Blockquote>
      )}
      {!showOnboardingForm && preparation && (
        <Stack>
          <Blockquote p="xs">
            <Title order={5}>Preparation instructions</Title>
            <MultilineText text={preparation} />
            <Text
              size="sm"
              c="dimmed"
              td="underline"
              onClick={() => setShowOnboardingForm(true)}
            >
              Edit
            </Text>
          </Blockquote>
        </Stack>
      )}
      {showOnboardingForm && (
        <form onSubmit={preparationForm.onSubmit(onSaveSessionPreparation)}>
          <Stack>
            <Textarea
              label="Preparation instructions"
              {...preparationForm.getInputProps("preparation")}
              autosize
              minRows={4}
              placeholder="Please provide instructions for the client to prepare for this session."
            />
            <Button type="submit" fullWidth>
              Save
            </Button>
          </Stack>
        </form>
      )}
      {futureSession && !showOnboardingForm && !preparation && (
        <Paper
          p="md"
          withBorder
          mb="sm"
          bg="blue.0"
          onClick={() => setShowOnboardingForm(true)}
        >
          <Flex gap="sm" align="center">
            <Box style={{ flex: "1 1 auto" }}>
              <Text>
                Click to provide preparation instructions for the client.
              </Text>
            </Box>
            <Box>
              <ActionIcon variant="transparent" size="lg" color="gray">
                <IconChevronRight />
              </ActionIcon>
            </Box>
          </Flex>
        </Paper>
      )}
    </Stack>
  );
};

const RequestedSession = (props: {
  session: SessionWithOwner;
  refetchSession: () => Promise<unknown>;
  children?: React.ReactNode;
}) => {
  const { session, children, refetchSession } = props;
  const { navigator, navigate } = useNavigator();
  const [
    openedDeclineModal,
    { open: openDeclineModal, close: closeDeclineModal },
  ] = useDisclosure(false);
  const [selectedDatetime, setSelectedDatetime] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  const confirmMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/confirm",
  );
  const declineMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/decline",
  );

  const onConfirm = async () => {
    if (!selectedDatetime) return;

    await confirmMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        scheduledAt: selectedDatetime,
      },
    });
    await refetchSession();
  };

  const onDecline = async () => {
    await declineMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        reason: declineReason,
      },
    });
    closeDeclineModal();
    navigate({ path: UniversalRoutes.SESSIONS.LIST });
  };

  return (
    <Stack>
      <Text fw="bold">Please select one option to confirm the session.</Text>
      <Text>{session.owner.firstName} proposed these dates and times:</Text>

      <Radio.Group value={selectedDatetime} onChange={setSelectedDatetime}>
        <Stack pt="md" gap="xs">
          {session.proposedTimes?.map((datetime) => {
            const parsedDatetime = df.parseISO(datetime);
            const timeStr = df.format(parsedDatetime, "MMM d, yyyy 'at' HH:mm");
            const endTimeStr = df.format(
              df.addMinutes(
                parsedDatetime,
                session.sessionType.durationMinutes,
              ),
              "HH:mm",
            );

            return (
              <Radio.Card radius="md" value={datetime} key={datetime}>
                <Group wrap="nowrap" align="flex-start" p="md">
                  <Radio.Indicator />
                  <Text lh="1.3">
                    {timeStr} - {endTimeStr}
                  </Text>
                </Group>
              </Radio.Card>
            );
          })}
        </Stack>
      </Radio.Group>

      <Stack mt="md">
        <Button
          fullWidth
          onClick={onConfirm}
          disabled={!selectedDatetime}
          loading={confirmMutation.isPending}
        >
          Confirm Selected Date and Time
        </Button>
        <Button
          fullWidth
          variant="outline"
          onClick={navigator({
            path: UniversalRoutes.CHAT.DETAIL,
            params: { engagementId: session.engagementId },
          })}
        >
          Message {session.owner.firstName}
        </Button>
        <Button fullWidth bg="red.8" onClick={openDeclineModal}>
          Decline Request
        </Button>
      </Stack>

      <Modal
        opened={openedDeclineModal}
        onClose={closeDeclineModal}
        title="Decline Session Request"
      >
        <Stack>
          <TextInput
            label="Message to client (optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.currentTarget.value)}
            placeholder="Please explain why you need to decline"
          />
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={closeDeclineModal}
              disabled={declineMutation.isPending}
            >
              Never mind
            </Button>
            <Button
              color="red.8"
              onClick={onDecline}
              loading={declineMutation.isPending}
            >
              Send Decline Message
            </Button>
          </Group>
        </Stack>
      </Modal>
      {children}
    </Stack>
  );
};

const ConfirmedSession = (props: {
  session: SessionWithOwner;
  children?: React.ReactNode;
}) => {
  const { session, children } = props;
  const { navigate } = useNavigator();
  const [
    openedCancelModal,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false);
  const [cancelReason, setCancelReason] = useState("");

  const cancelMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/cancel",
  );

  const onCancel = async () => {
    await cancelMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        reason: cancelReason,
      },
    });
    closeCancelModal();
    navigate({ path: UniversalRoutes.HOME });
  };

  return (
    <Stack>
      {session.scheduledAt && (
        <Text>
          {"Scheduled for "}
          {df.format(df.parseISO(session.scheduledAt), "MMM d, yyyy h:mm a")}
        </Text>
      )}
      {children}
      <Button color="red.8" onClick={openCancelModal} mt="sm">
        Cancel Session
      </Button>

      <Modal
        opened={openedCancelModal}
        onClose={closeCancelModal}
        title="Cancel Session"
      >
        <Stack>
          <TextInput
            label="Message to client (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            placeholder="Please explain why you need to cancel"
          />
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={closeCancelModal}
              disabled={cancelMutation.isPending}
            >
              Never mind
            </Button>
            <Button
              color="red.8"
              onClick={onCancel}
              loading={cancelMutation.isPending}
            >
              Send Cancellation
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

const CompletedSession = (props: {
  session: SessionWithOwner;
  children?: React.ReactNode;
}) => {
  const { session, children } = props;
  return (
    <Stack>
      <Text>
        {"Session completed on "}
        {session.scheduledAt &&
          df.format(df.parseISO(session.scheduledAt), "MMM d, yyyy h:mm a")}
      </Text>
      {children}
    </Stack>
  );
};

const CancelledSession = (props: {
  session: SessionWithOwner;
  children?: React.ReactNode;
}) => {
  const { session, children } = props;
  return (
    <Stack>
      {session.scheduledAt && (
        <Text>
          {"Original scheduled for "}
          {df.format(df.parseISO(session.scheduledAt), "MMM d, yyyy h:mm a")}
        </Text>
      )}
      {session.createdAt && (
        <Text>
          {"Requested on "}
          {df.format(df.parseISO(session.createdAt), "MMM d, yyyy h:mm a")}
        </Text>
      )}
      {renderCancelStatus(session.status, "trainer")}
      {session.reason && (
        <Blockquote>
          <Text>{session.reason}</Text>
        </Blockquote>
      )}
      {children}
    </Stack>
  );
};

export const Session = () => {
  const { navigator, navigate } = useNavigator();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { activeTab, onTabChange } = useHashTab<SessionTab>(
    SessionTab.PREPARATION,
  );

  const { data: session, refetch: refetchSession } = client.useQuery(
    "get",
    "/trainer/sessions/{sessionId}",
    {
      params: {
        path: {
          sessionId,
        },
      },
    },
  );

  // Get survey statuses for this session's files
  const { data: engagement } = client.useQuery(
    "get",
    "/trainer/engagements/{engagementId}",
    {
      params: {
        path: {
          engagementId: session?.engagementId || "",
        },
      },
    },
    { enabled: !!session?.engagementId },
  );

  // Fetch files for the session at the top level
  const fileIds = session?.fileIds ?? [];
  const {
    data: fileList,
    isLoading: isLoadingFiles,
    refetch: refetchFiles,
  } = client.useQuery(
    "get",
    "/universal/files-batch/batch-get",
    {
      params: {
        query: {
          fileIds: fileIds,
        },
      },
    },
    {
      enabled: fileIds.length > 0,
    },
  );

  const handleSurveyClick = (surveyId: string, status: SurveyStatus) => {
    if (!session) {
      return;
    }

    // Don't open not filled surveys
    if (status !== "FILLED") {
      return;
    }

    // Navigate to dedicated survey results page
    navigate({
      path: TrainerRoutes.SURVEYS.RESULTS,
      params: {
        engagementId: session.engagementId,
        surveyId: surveyId,
      },
    });
  };

  const deleteFileMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/remove-file",
  );

  const handleDeleteSurvey = (fileId: string) => {
    if (!session) {
      return;
    }

    modals.openConfirmModal({
      title: "Remove Survey",
      children: (
        <Text size="sm">
          Are you sure you want to remove this survey from the session? All
          responses will be lost.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteFileMutation.mutateAsync({
            params: {
              path: {
                sessionId: session.id,
              },
            },
            body: { fileId: fileId },
          });
          await Promise.all([refetchFiles(), refetchSession()]);
        } catch (error) {
          console.error("Failed to delete survey:", error);
        }
      },
    });
  };

  const addFileToSessionMutation = client.useMutation(
    "post",
    "/trainer/sessions/{sessionId}/add-file",
  );

  const handleAddSurveyToSession = async (file: FileOutput) => {
    if (!session) return;

    try {
      await addFileToSessionMutation.mutateAsync({
        params: {
          path: {
            sessionId: session.id,
          },
        },
        body: {
          fileId: file.id,
        },
      });

      await Promise.all([refetchFiles(), refetchSession()]);
    } catch (error) {
      console.error("Error adding survey to session:", error);
    }
  };

  const hasFiles = session?.fileIds && session.fileIds.length > 0;

  if (!(session && engagement && (fileList || !hasFiles))) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Header title="View Session" />

      <Flex
        gap="sm"
        align="center"
        onClick={navigator({
          path: TrainerRoutes.CLIENTS.DETAIL,
          params: { engagementId: session.engagementId },
        })}
      >
        <Portrait profile={session.owner} size="lg" />
        <Portrait profile={session.dog} size="lg" who={"dog"} />
        <Box style={{ flex: "1 1 auto" }}>
          <Text fw="bold">{session.sessionType.name}</Text>
          <Text>
            with {session.owner.firstName} {session.owner.lastName}
          </Text>
          <Text>and {session.dog.name}</Text>
        </Box>
      </Flex>

      {session.status === "REQUESTED" && (
        <RequestedSession session={session} refetchSession={refetchSession} />
      )}

      {session.status === "CONFIRMED" && <ConfirmedSession session={session} />}
      {session.status === "COMPLETED" && <CompletedSession session={session} />}
      {isSessionCancelled(session.status) && (
        <CancelledSession session={session} />
      )}
      {session.status === "COMPLETED" ||
        (session.status === "CONFIRMED" && (
          <SessionTabs activeTab={activeTab} onTabChange={onTabChange}>
            {activeTab === SessionTab.PREPARATION && (
              <SessionNotes session={session} />
            )}

            {activeTab === SessionTab.MATERIALS && (
              <SessionFiles
                session={session}
                refetchSession={refetchSession}
                fileList={fileList}
                refetchFiles={refetchFiles}
                isLoadingFiles={isLoadingFiles}
              />
            )}

            {activeTab === SessionTab.SURVEYS && (
              <Stack>
                <Group>
                  <FolderNavigator
                    onFileSelect={handleAddSurveyToSession}
                    allowedFileTypes={["SURVEY"]}
                  >
                    <Button
                      variant="outline"
                      leftSection={<IconFolder size="1.2em" />}
                      style={{ flex: 1 }}
                    >
                      Add Survey from Library
                    </Button>
                  </FolderNavigator>
                </Group>

                <SurveyList
                  files={
                    fileList?.files?.filter((file) => file.type === "SURVEY") ||
                    []
                  }
                  surveyStatuses={engagement?.surveys || {}}
                  onSurveyClick={handleSurveyClick}
                  isLoading={isLoadingFiles && !!session}
                  onDelete={handleDeleteSurvey}
                />
              </Stack>
            )}
          </SessionTabs>
        ))}
    </Stack>
  );
};
